import { HeroMinimal, BookingCTA } from "../components/sections";
import { SectionHeader, FeatureCard } from "../components/sections";
import { Brain, Users, Zap, CheckCircle } from "lucide-react";

const values = [
  {
    title: "Systems Over Campaigns",
    description:
      "We build continuous, compounding infrastructure rather than one-off campaigns that decay.",
  },
  {
    title: "Outcomes Over Activity",
    description:
      "Qualified conversations and pipeline are the only metrics that matter. Everything else is noise.",
  },
  {
    title: "Specificity Over Hype",
    description:
      'We explain exactly how the system works. No "AI magic" black boxes. No vague promises.',
  },
  {
    title: "Leverage Over Labor",
    description:
      "Automation and AI should multiply human capacity, not replace human judgment.",
  },
  {
    title: "Trust Through Transparency",
    description:
      "We show our work. No fabricated proof. No hidden methodology. Clear limitations.",
  },
  {
    title: "Long-Term Partnership",
    description:
      "We succeed when your outbound system becomes a durable competitive advantage.",
  },
];

const philosophyPoints = [
  "Automation creates leverage. AI increases intelligence. Humans close the relationship.",
  "Systems compound. Campaigns decay.",
  "Better data beats more data.",
  "Infrastructure enables scale. Strategy directs it.",
  "Qualification is the product. Outreach is the delivery mechanism.",
  "The best outbound systems start with a strong foundation.",
];

const teamPrinciples = [
  {
    title: "Sales DNA",
    description:
      "Built by people who have carried quotas, managed SDR teams, and closed enterprise deals.",
  },
  {
    title: "Technical Depth",
    description:
      "Engineers who understand infrastructure, data pipelines, ML systems, and API architectures.",
  },
  {
    title: "B2B Focus",
    description:
      "Exclusively B2B technology. We don't do B2C, e-commerce, or generic lead gen.",
  },
  {
    title: "No Black Boxes",
    description:
      "Clients understand their system architecture. We document and transfer knowledge.",
  },
];

function AboutPage() {
  return (
    <>
      <HeroMinimal
        title="Why UniqReach Exists"
        subtitle="A technology-enabled sales infrastructure company with human expertise behind it. Built for B2B technology companies that need predictable, scalable pipeline."
        ctaText="Book a Strategy Call"
        ctaHref="/book"
        secondaryCtaText="See How It Works"
        secondaryCtaHref="/how-it-works"
      />

      {/* Origin Story */}
      <section className="section" aria-labelledby="origin-title">
        <div className="container">
          <SectionHeader
            id="origin-title"
            eyebrow="Origin"
            title="The Problem We Were Built to Solve"
            description="B2B technology companies have great products but struggle to consistently reach the right buyers. The market offered only two options: generic agencies that send emails, or complex tools that require teams to operate. We built the third option."
            align="center"
          />
          <div className="max-w-3xl mx-auto mt-12 space-y-6 text-center">
            <div className="card-elevated p-8">
              <p className="body-lg text-text-secondary mb-6">
                "Companies should spend more time closing qualified
                opportunities and less time searching for them."
              </p>
              <p className="body text-text-secondary">
                This philosophy drives every architectural decision: from our
                verification-first data approach to our AI-native workflows to
                our infrastructure discipline. The system exists to create
                leverage for your sales team rather than generate activity
                reports.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Core Philosophy */}
      <section
        className="section bg-surface border-y border-border"
        aria-labelledby="philosophy-title"
      >
        <div className="container">
          <SectionHeader
            id="philosophy-title"
            eyebrow="Philosophy"
            title="Automation Creates Leverage. AI Increases Intelligence. Humans Close the Relationship."
            description="Three layers. Clear boundaries. Each amplifies the other."
            align="center"
          />
          <div className="grid md:grid-cols-3 gap-6 mt-12 max-w-5xl mx-auto">
            {[
              {
                icon: Zap,
                title: "Automation",
                desc: "Workflow automation removes repetitive manual work: research, enrichment, personalization, sequencing, CRM sync, reporting.",
              },
              {
                icon: Brain,
                title: "AI Intelligence",
                desc: "AI handles research, classification, scoring, content generation, and optimization, handling intelligence-heavy repetitive work.",
              },
              {
                icon: Users,
                title: "Human Judgment",
                desc: "Strategy, positioning, messaging direction, quality control, sales conversations, relationship building, closing.",
              },
            ].map((item, index) => (
              <div
                key={item.title}
                className="card-elevated p-8 text-center animate-slide-up"
                style={{ animationDelay: `${index * 100}ms` }}
              >
                <div className="w-16 h-16 rounded-xl bg-accent/10 flex items-center justify-center mx-auto mb-4 text-accent">
                  <item.icon size={32} aria-hidden="true" />
                </div>
                <h3 className="h2 text-text-primary mb-3">{item.title}</h3>
                <p className="body text-text-secondary">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Principles */}
      <section className="section" aria-labelledby="principles-title">
        <div className="container">
          <SectionHeader
            id="principles-title"
            eyebrow="Principles"
            title="Decision-Making Framework"
            description="These principles guide every system design decision, every client engagement, and every product choice."
            align="center"
          />
          <div className="max-w-3xl mx-auto mt-12 space-y-4">
            {philosophyPoints.map((point, index) => (
              <div
                key={index}
                className="card p-5 flex items-start gap-4 animate-slide-up"
                style={{ animationDelay: `${index * 100}ms` }}
              >
                <div className="w-10 h-10 rounded-lg bg-accent/10 flex items-center justify-center flex-shrink-0 text-accent">
                  <CheckCircle size={20} aria-hidden="true" />
                </div>
                <p className="body text-text-secondary pt-1">{point}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Values */}
      <section
        className="section bg-surface border-y border-border"
        aria-labelledby="values-title"
      >
        <div className="container">
          <SectionHeader
            id="values-title"
            eyebrow="Values"
            title="What We Stand For"
            description="Principles put into practice. These values show up in how we build, sell, and deliver."
            align="center"
          />
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mt-12">
            {values.map((value, index) => (
              <FeatureCard
                key={value.title}
                title={value.title}
                description={value.description}
                className="animate-slide-up"
                style={{ animationDelay: `${index * 100}ms` }}
              />
            ))}
          </div>
        </div>
      </section>

      {/* Team Approach */}
      <section className="section" aria-labelledby="team-title">
        <div className="container">
          <SectionHeader
            id="team-title"
            eyebrow="Team"
            title="Built by Practitioners"
            description="We're not a generic agency that added AI. We're sales practitioners and engineers who built the system we wished existed."
            align="center"
          />
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mt-12 max-w-5xl mx-auto">
            {teamPrinciples.map((principle, index) => (
              <div
                key={principle.title}
                className="card-elevated p-6 text-center animate-slide-up"
                style={{ animationDelay: `${index * 100}ms` }}
              >
                <h3 className="h3 text-text-primary mb-2">{principle.title}</h3>
                <p className="body text-text-secondary">
                  {principle.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Focus */}
      <section
        className="section bg-surface border-y border-border"
        aria-labelledby="focus-title"
      >
        <div className="container">
          <SectionHeader
            id="focus-title"
            eyebrow="Focus"
            title="Exclusively B2B Technology"
            description="We don't do B2C, e-commerce, local services, or generic lead generation. Our systems are designed for the complexity of B2B technology sales."
            align="center"
          />
          <div className="max-w-3xl mx-auto mt-12 space-y-6">
            {[
              "Long sales cycles with multiple stakeholders",
              "Technical buyers who require relevant context",
              "High customer lifetime value justifying investment",
              "Identifiable ICP with firmographic/technographic signals",
              "Need for repeatable, scalable pipeline generation",
              "Complex value propositions requiring intelligent personalization",
            ].map((item, index) => (
              <div
                key={index}
                className="card p-5 flex items-center gap-3 animate-slide-up"
                style={{ animationDelay: `${index * 50}ms` }}
              >
                <CheckCircle
                  size={20}
                  className="text-accent flex-shrink-0"
                  aria-hidden="true"
                />
                <p className="body text-text-secondary">{item}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <BookingCTA />
    </>
  );
}

export default AboutPage;
